import React, { useState, useEffect, useMemo, useCallback } from "react";
import { X, Users, Search, User, MapPin, Building2 } from "lucide-react";
import axiosInstance from "../../../../axiosInstance";
import { toast } from "react-toastify";
import ColoredCheckbox from "../../../../components/ColoredCheckbox/ColoredCheckbox";
import ButtonWithIcon from "../../../../components/ButtonwithIcon/ButtonWithIcon";
import "./EmployeeAssignmentModal.scss";

const EmployeeAssignmentModal = ({
  isOpen,
  onClose,
  selectedShifts = [],
  onSuccess,
}) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the branch IDs from the selected office timing
  const selectedShiftBranchIds = useMemo(() => {
    if (!selectedShifts || selectedShifts.length === 0) return [];
    const shift = selectedShifts[0];
    return shift.branches?.map((b) => b.branchId) || [];
  }, [selectedShifts]);

  // Fetch all employees
  const fetchEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/employees/All`
      );

      if (response.status === 200) {
        const employeesData = response.data.data || [];
        const normalized = Array.isArray(employeesData) ? employeesData : [];

        // Filter only ENABLED employees (case-insensitive)
        const enabledEmployees = normalized.filter(
          (emp) => emp.status && emp.status.toUpperCase() === "ENABLED"
        );

        setEmployees(enabledEmployees);

        // Pre-select employees already assigned to this office timing
        // by checking if their officeTimingId matches the selected shift
        if (selectedShifts && selectedShifts.length > 0) {
          const currentOfficeTimingId = selectedShifts[0].officeTimingId;
          const assignedEmployees = enabledEmployees
            .filter(
              (emp) =>
                Number(emp.officeTimingId) === Number(currentOfficeTimingId)
            )
            .map((emp) => emp.empId);
          setSelectedEmployeeIds(assignedEmployees);
        }
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees. Please try again.");
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedShifts]);

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
      setSearchTerm("");
    } else {
      // Reset on close
      setSelectedEmployeeIds([]);
      setSearchTerm("");
    }
  }, [isOpen, fetchEmployees]);

  // Filter employees by branch and search term
  const filteredEmployees = useMemo(() => {
    if (selectedShiftBranchIds.length === 0) return [];

    return employees.filter((employee) => {
      // Filter by branch - only show employees from branches assigned to this office timing
      const employeeBranchId = employee.branch?.branchId;
      if (!selectedShiftBranchIds.includes(employeeBranchId)) {
        return false;
      }

      // Filter by search term
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${employee.firstName || ""} ${
          employee.lastName || ""
        }`.toLowerCase();
        const employeeId = (employee.empId || "").toString().toLowerCase();
        const email = (
          employee.companyEmail ||
          employee.email ||
          ""
        ).toLowerCase();
        const department = (
          employee.departments?.[0]?.departmentName || ""
        ).toLowerCase();
        const branch = (employee.branch?.branchName || "").toLowerCase();

        return (
          fullName.includes(searchLower) ||
          employeeId.includes(searchLower) ||
          email.includes(searchLower) ||
          department.includes(searchLower) ||
          branch.includes(searchLower)
        );
      }

      return true;
    });
  }, [employees, selectedShiftBranchIds, searchTerm]);

  const isAllFilteredSelected = useMemo(() => {
    if (filteredEmployees.length === 0) return false;
    return filteredEmployees.every((emp) =>
      selectedEmployeeIds.includes(emp.empId)
    );
  }, [filteredEmployees, selectedEmployeeIds]);

  const handleEmployeeSelect = (employeeId) => {
    setSelectedEmployeeIds((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredEmployees.map((emp) => emp.empId);
    if (isAllFilteredSelected) {
      // Deselect all that are currently filtered
      setSelectedEmployeeIds((prev) =>
        prev.filter((id) => !filteredIds.includes(id))
      );
    } else {
      // Select all that are currently filtered, without duplicates
      setSelectedEmployeeIds((prev) => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedEmployeeIds.length === 0) {
      toast.error("Please select at least one employee.");
      return;
    }

    setIsSubmitting(true);
    try {
      const officeTimingId = selectedShifts[0].officeTimingId;

      // POST request to assign employees
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/office-timings/${officeTimingId}/employees`,
        selectedEmployeeIds
      );

      if (response.status === 200) {
        toast.success(
          `Successfully assigned ${selectedEmployeeIds.length} employee${
            selectedEmployeeIds.length !== 1 ? "s" : ""
          } to ${selectedShifts[0].shiftName}`
        );

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }

        onClose();
      }
    } catch (error) {
      console.error("Error in employee assignment:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to assign employees. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderEmployeeCard = (employee) => {
    const isSelected = selectedEmployeeIds.includes(employee.empId);
    const isCurrentlyAssigned =
      Number(employee.officeTimingId) ===
      Number(selectedShifts[0]?.officeTimingId);

    return (
      <div
        key={employee.empId}
        className={`employee-assignment-modal__employee-card ${
          isSelected ? "selected" : ""
        }`}
        onClick={() => handleEmployeeSelect(employee.empId)}
      >
        <div className="employee-assignment-modal__employee-header">
          <div className="employee-assignment-modal__employee-info">
            <h4 className="employee-assignment-modal__employee-name">
              {employee.firstName} {employee.lastName}
              {isCurrentlyAssigned && (
                <span className="employee-assignment-modal__current-badge">
                  Current
                </span>
              )}
            </h4>
            <div className="employee-assignment-modal__employee-meta">
              <span className="employee-assignment-modal__employee-id">
                ID: {employee.empId}
              </span>
              {employee.branch?.branchName && (
                <span className="employee-assignment-modal__employee-branch">
                  <MapPin className="h-3 w-3" />
                  {employee.branch.branchName}
                </span>
              )}
            </div>
            {employee.companyEmail && (
              <div className="employee-assignment-modal__employee-email">
                {employee.companyEmail}
              </div>
            )}
            {employee.departments && employee.departments.length > 0 && (
              <div className="employee-assignment-modal__employee-department">
                {employee.departments[0].departmentName}
              </div>
            )}
          </div>

          <div className="employee-assignment-modal__employee-checkbox">
            <ColoredCheckbox
              checked={isSelected}
              onChange={() => handleEmployeeSelect(employee.empId)}
              color="green"
              name={`employee-${employee.empId}`}
            />
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  if (!selectedShifts || selectedShifts.length === 0) return null;

  const selectedShift = selectedShifts[0];
  const branchNames = selectedShift?.branches
    ?.map((b) => b.branchName)
    .join(", ");

  return (
    <div className="employee-assignment-modal-overlay" onClick={onClose}>
      <div
        className="employee-assignment-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="employee-assignment-modal__header">
          <div className="employee-assignment-modal__title-section">
            <Users className="employee-assignment-modal__icon" />
            <div>
              <h2 className="employee-assignment-modal__title">
                Assign Employees to Office Timing
              </h2>
              <p className="employee-assignment-modal__subtitle">
                Select employees to assign to this office timing
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="employee-assignment-modal__close-btn"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="employee-assignment-modal__form"
        >
          <div className="employee-assignment-modal__content">
            {/* Office Timing Info */}
            <div className="employee-assignment-modal__timings-preview">
              <h3 className="employee-assignment-modal__preview-title">
                <Building2 className="h-4 w-4" />
                {selectedShift?.shiftName || "N/A"}
              </h3>
              <div className="employee-assignment-modal__preview-details">
                <span>
                  Time: {selectedShift?.shiftForm || "N/A"} -{" "}
                  {selectedShift?.shiftTo || "N/A"}
                </span>
                {branchNames && (
                  <span className="employee-assignment-modal__preview-branches">
                    Branches: {branchNames}
                  </span>
                )}
              </div>
            </div>

            {/* Search Section */}
            <div className="employee-assignment-modal__search-section">
              <div className="employee-assignment-modal__search-container">
                <Search className="employee-assignment-modal__search-icon" />
                <input
                  type="text"
                  placeholder="Search employees by name, email, ID, or department..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="employee-assignment-modal__search-input"
                />
              </div>

              <div className="employee-assignment-modal__selection-summary">
                <span className="employee-assignment-modal__selected-count">
                  {selectedEmployeeIds.length} of {filteredEmployees.length}{" "}
                  employee(s) selected
                </span>
                {filteredEmployees.length > 0 && (
                  <ButtonWithIcon
                    icon={
                      isAllFilteredSelected
                        ? "fluent:checkbox-checked-20-filled"
                        : "fluent:checkbox-unchecked-20-regular"
                    }
                    text={isAllFilteredSelected ? "Deselect All" : "Select All"}
                    onClick={handleSelectAllFiltered}
                    className="secondary-button"
                    type="button"
                  />
                )}
              </div>
            </div>

            {/* Employees List */}
            <div className="employee-assignment-modal__employees-section">
              <h4 className="employee-assignment-modal__employees-title">
                Available Employees ({filteredEmployees.length})
              </h4>
              {isLoading ? (
                <div className="employee-assignment-modal__loading">
                  <div className="employee-assignment-modal__loading-spinner"></div>
                  <span>Loading employees...</span>
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="employee-assignment-modal__no-employees">
                  <User className="employee-assignment-modal__no-employees-icon" />
                  <p>
                    {searchTerm
                      ? "No employees match your search criteria."
                      : selectedShiftBranchIds.length === 0
                      ? "No branches assigned to this office timing."
                      : "No employees found for the selected branch(es)."}
                  </p>
                </div>
              ) : (
                <div className="employee-assignment-modal__employees-grid">
                  {filteredEmployees.map(renderEmployeeCard)}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="employee-assignment-modal__footer">
            <ButtonWithIcon
              icon="mdi:close"
              text="Cancel"
              onClick={onClose}
              className="cancel-button"
              disabled={isSubmitting}
              type="button"
            />
            <ButtonWithIcon
              icon="mdi:account-check"
              text={
                isSubmitting
                  ? "Assigning..."
                  : `Assign ${selectedEmployeeIds.length} Employee${
                      selectedEmployeeIds.length !== 1 ? "s" : ""
                    }`
              }
              onClick={handleSubmit}
              className="submit-button"
              disabled={isSubmitting || selectedEmployeeIds.length === 0}
              type="submit"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeAssignmentModal;
